import clsx from 'clsx';
import { motion } from 'framer-motion';
import {
  DownloadPresentationAsPNG,
  DownloadPresentationAsPNGProps,
  PresentationClientInformation,
  PresentationFirmLogoSidebar,
  PresentationFirmLogoSidebarSkeleton,
  PresentationScorecardTargetScores,
  PresentationViewOptions,
} from './components';
import { useFullScreenScorecardPage, UseFullScreenScorecardPageArgs } from './hooks';
import { FC } from 'react';
import {
  AdjustedAnnualSpendingToggle,
  AnnualIncomeWidget,
  AnnualSpendingWidget,
  ElementRoutes,
  NetWorthWidget,
  ScorecardSkeleton,
} from '../../components';
import { LocalThemeContextProvider } from '../../context';
import { FullScreenScorecardContextProvider, PresentationViewOptionsContextProvider } from './context';
import { Outlet, useNavigate } from '@remix-run/react';

import { darkModeColors, SCORECARD_ELEMENT_SYMBOLS } from '../../..';
import { elementShortcutMap, otherShortcutMap } from './util';
import { useHotkeys } from 'react-hotkeys-hook';
import { useKeyPress } from 'react-use';

interface FullScreenScorecardPageProps
  extends UseFullScreenScorecardPageArgs,
    Pick<DownloadPresentationAsPNGProps, 'onDownloadAll'> {
  viewOnly?: boolean;
}

export const FullScreenScorecardPage: FC<FullScreenScorecardPageProps> = ({
  firmID,
  clientID,
  viewOnly,
  pathName,
  onDownloadAll,
  householdID,
  fullScreenRegex,
}) => {
  const presentationWidth = 1920;
  const presentationHeight = 1080;
  const {
    scorecard,
    firmClient,
    allFocused,
    scaleFactor,
    riskFocused,
    viewOptions,
    scorecardRef,
    firmBranding,
    firmHasNoLogo,
    assetsFocused,
    scorecardQuery,
    cashFlowFocused,
    headsOfHousehold,
    firmLogoImageRef,
    firmBrandingQuery,
    fullPresentationRef,
    toggleShowClientAge,
    toggleShowClientName,
    toggleElementVisibility,
    toggleShowFinancialDetails,
    toggleAllElementsVisibility,
    toggleRiskElementsVisibility,
    toggleAssetsElementsVisibility,
    toggleCashFlowElementsVisibility,
    toggleShowEmojis,
    toggleShowTargetScores,
    focusElementVisibility,
    shortcutsEnabled,
    allowEstateStatus,
    estateStatusChecklistItems,
  } = useFullScreenScorecardPage({ clientID, firmID, householdID, pathName, fullScreenRegex });
  const presentationScaleFactor = scaleFactor ?? 1;

  type ShortcutKeys = keyof typeof otherShortcutMap;

  const keyToFunction: {
    [key in ShortcutKeys]: () => void;
  } = {
    toggleAllElements: toggleAllElementsVisibility,
    toggleCashFlowElements: toggleCashFlowElementsVisibility,
    toggleRiskElements: toggleRiskElementsVisibility,
    toggleAssetsElements: toggleAssetsElementsVisibility,
    toggleTargetScores: toggleShowTargetScores,
    toggleEmojis: toggleShowEmojis,
    toggleClientName: toggleShowClientName,
    toggleClientAge: toggleShowClientAge,
    toggleFinancialDetails: toggleShowFinancialDetails,
  };
  const routes: ElementRoutes = {
    Er: 'equity-rate',
    Ir: 'insurance-rate',
    ...(allowEstateStatus ? { Es: 'estate-status' } : {}),
    Sr: 'savings-rate',
    Br: 'burn-rate',
    Dr: 'debt-rate',
    Tr: 'tax-rate',
    Lt: 'liquid-term',
    Qt: 'qualified-term',
    Rt: 'real-estate-term',
    Bt: 'business-term',
    Tt: 'total-term',
  };

  const nav = useNavigate();

  //disable element toggle when enter is held
  const isEnterHeld = useKeyPress('Enter');

  SCORECARD_ELEMENT_SYMBOLS.forEach((element) => {
    const isElementEnabled = element !== 'Es' || allowEstateStatus === true;

    useHotkeys(
      `${elementShortcutMap[element]}`,
      () => {
        toggleElementVisibility(element);
      },
      { enabled: isElementEnabled && shortcutsEnabled && !isEnterHeld[0] }
    );

    useHotkeys(
      `shift+${elementShortcutMap[element]}`,
      () => {
        focusElementVisibility(element);
      },
      { enabled: isElementEnabled && shortcutsEnabled }
    );
    useHotkeys(
      `enter+${elementShortcutMap[element]}`,
      () => {
        if (routes[element]) {
          nav(routes[element]);
        }
      },
      { enabled: isElementEnabled && shortcutsEnabled }
    );
  });

  Object.keys(otherShortcutMap).forEach((key) => {
    const shortcut = otherShortcutMap[key as ShortcutKeys];
    const action = keyToFunction[key as ShortcutKeys];

    if (action && shortcut) {
      useHotkeys(`${shortcut}`, () => {
        if (shortcutsEnabled) action();
      });
    }
  });

  return (
    <FullScreenScorecardContextProvider householdID={householdID} clientID={clientID} firmID={firmID}>
      <LocalThemeContextProvider paletteSeedColor={firmBranding?.color ?? darkModeColors.accent}>
        <PresentationViewOptionsContextProvider
          allFocused={allFocused}
          viewOptions={viewOptions}
          riskFocused={riskFocused}
          assetsFocused={assetsFocused}
          cashFlowFocused={cashFlowFocused}
          toggleShowClientAge={toggleShowClientAge}
          toggleShowClientName={toggleShowClientName}
          toggleElementVisibility={toggleElementVisibility}
          toggleShowFinancialDetails={toggleShowFinancialDetails}
          toggleAllElementsVisibility={toggleAllElementsVisibility}
          toggleRiskElementsVisibility={toggleRiskElementsVisibility}
          toggleAssetsElementsVisibility={toggleAssetsElementsVisibility}
          toggleCashFlowElementsVisibility={toggleCashFlowElementsVisibility}
          toggleShowEmojis={toggleShowEmojis}
          toggleShowTargetScores={toggleShowTargetScores}
        >
          {!viewOnly && firmBranding && (
            <div className="fixed right-8 top-8 flex z-10">
              <div className="mt-[10px]">
                <AdjustedAnnualSpendingToggle householdID={householdID} placement="bottom" offset={[-144, 8]} />
              </div>

              <PresentationViewOptions />
              {firmClient && scorecard && (
                <DownloadPresentationAsPNG
                  firmClient={firmClient}
                  firmBranding={firmBranding}
                  scorecardRef={scorecardRef}
                  onDownloadAll={onDownloadAll}
                  firmLogoImageRef={firmLogoImageRef}
                  fullPresentationRef={fullPresentationRef}
                />
              )}
            </div>
          )}

          <div className="grid h-screen w-screen place-content-center overflow-hidden">
            <div
              className="relative"
              style={{
                width: presentationWidth * presentationScaleFactor,
                height: presentationHeight * presentationScaleFactor,
              }}
            >
              <div
                className="absolute left-0 top-0 h-[1080px] w-[1920px]"
                style={{
                  transform: `scale(${presentationScaleFactor})`,
                  transformOrigin: 'top left',
                }}
              >
                <div
                  ref={fullPresentationRef}
                  className="grid h-[1080px] w-[1920px] grid-flow-col"
                  style={{
                    gridTemplateColumns: firmHasNoLogo ? '1fr' : 'auto 1fr',
                  }}
                >
                  {firmBrandingQuery.isLoading ? (
                    <PresentationFirmLogoSidebarSkeleton />
                  ) : firmBranding?.logo ? (
                    <PresentationFirmLogoSidebar ref={firmLogoImageRef} firmBranding={firmBranding} />
                  ) : null}
                  <div
                    ref={scorecardRef}
                    className={clsx(
                      'grid place-content-center bg-darkMode-gray-dark px-[116px]',
                      firmHasNoLogo ? 'w-[1920px]' : 'w-[1376px]'
                    )}
                  >
                    {firmClient && (
                      <PresentationClientInformation firmClient={firmClient} headsOfHousehold={headsOfHousehold} />
                    )}

                    {scorecardQuery.isLoading || !scorecard ? (
                      <ScorecardSkeleton
                        className={clsx(
                          '!gap-4',
                          '[&_.ScorecardSkeleton-button]:!w-[216px] [&_.ScorecardSkeleton-button]:!h-[216px] [&_.ScorecardSkeleton-button]:!rounded-[20px]'
                        )}
                      />
                    ) : (
                      <PresentationScorecardTargetScores
                        routes={routes}
                        scorecard={scorecard}
                        estateStatusChecklistItems={estateStatusChecklistItems}
                      />
                    )}
                    {viewOptions.showFinancialDetails && (
                      <motion.div layout className="grid grid-flow-col gap-4 mt-4 w-full">
                        <NetWorthWidget householdID={householdID} />
                        <AnnualIncomeWidget householdID={householdID} linkTo={'income-and-spending'} />
                        <AnnualSpendingWidget householdID={householdID} linkTo={'income-and-spending'} />
                      </motion.div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </PresentationViewOptionsContextProvider>
      </LocalThemeContextProvider>
      <Outlet />
    </FullScreenScorecardContextProvider>
  );
};
